import React, { useCallback, useEffect, useRef, useState } from "react";

/**
 * Narzędzia pomiaru (odległość + powierzchnia) dla OpenLayers.
 *
 * Dodaje warstwę wektorową + interakcję Draw na podaną mapę. UI: panel
 * w prawym górnym rogu mapy z trzema przyciskami:
 *   📏 Odległość (LineString) · ⬟ Powierzchnia (Polygon) · 🗑 Wyczyść
 *
 * UX:
 *   - Klik w aktywny tryb wyłącza rysowanie
 *   - Double-click / Enter kończy pomiar (domyślne zachowanie Draw)
 *   - Escape przerywa rysowanie bez zapisu
 *   - Każda zmierzona geometria zostaje na mapie z persistent etykietą;
 *     Podczas rysowania — live tooltip przy kursorze z bieżącym wynikiem.
 *   - Pomiary są "geodezyjne" (ol/sphere.getLength/getArea) — dają dystans
 *     na elipsoidzie (WGS84), niezależnie od projekcji mapy (2180/3857/etc.).
 *
 * Integracja: renderowany wewnątrz komponentów mapy (OlMap, PlanPreviewMap,
 * RasterPreviewMap), po tym jak instance `ol/Map` jest gotowy.
 */

export type MeasureMode = "off" | "distance" | "area";

export type MeasureToolsProps = {
  /** Instance ol/Map (null → jeszcze nie ready; komponent nic nie renderuje). */
  map: any | null;
  /** Nadpisz pozycję panelu przycisków. Default: prawy górny róg mapy. */
  style?: React.CSSProperties;
  /**
   * Osadzone w innym kontenerze (np. FloatingPanel) — bez `position: absolute`,
   * bez własnego borderu/tła. Tylko flex-row z przyciskami.
   */
  embedded?: boolean;
};

function MeasureTools({ map, style, embedded }: MeasureToolsProps) {
  const [mode, setMode] = useState<MeasureMode>("off");
  const [hasMeasurements, setHasMeasurements] = useState(false);

  // OL refs — tworzone raz, po dynamic-import OL w useEffect.
  const olLibsRef = useRef<any>(null);
  const vectorSourceRef = useRef<any>(null);
  const vectorLayerRef = useRef<any>(null);
  const drawInteractionRef = useRef<any>(null);
  // Overlay podążający za kursorem podczas rysowania (live measurement).
  const cursorOverlayRef = useRef<any>(null);
  // Overlay'e finalnych etykiet (po drawend) — do posprzątania przy "Wyczyść".
  const finalOverlaysRef = useRef<any[]>([]);
  // Listener ESC — do posprzątania przy zmianie trybu / unmount.
  const escHandlerRef = useRef<((e: KeyboardEvent) => void) | null>(null);

  // ─────── Init: warstwa pomiaru + overlay kursora ───────
  useEffect(() => {
    if (!map) return;
    let cancelled = false;

    (async () => {
      const [olStyle, olLayerVector, olSourceVector, olOverlay] = await Promise.all([
        import("ol/style"),
        import("ol/layer/Vector"),
        import("ol/source/Vector"),
        import("ol/Overlay"),
      ]);
      const olInteraction = await import("ol/interaction");
      const olSphere = await import("ol/sphere");
      if (cancelled) return;

      olLibsRef.current = {
        Style: olStyle.Style,
        Fill: olStyle.Fill,
        Stroke: olStyle.Stroke,
        CircleStyle: olStyle.Circle,
        VectorLayer: olLayerVector.default,
        VectorSource: olSourceVector.default,
        Overlay: olOverlay.default,
        Draw: olInteraction.Draw,
        getLength: olSphere.getLength,
        getArea: olSphere.getArea,
      };

      const { Style, Fill, Stroke, CircleStyle, VectorSource, VectorLayer } = olLibsRef.current;
      const source = new VectorSource({ wrapX: false });
      const layer = new VectorLayer({
        source,
        zIndex: 9_999,
        style: new Style({
          fill: new Fill({ color: "rgba(37, 99, 235, 0.18)" }),
          stroke: new Stroke({ color: "#1d4ed8", width: 2.5 }),
          image: new CircleStyle({
            radius: 5,
            fill: new Fill({ color: "#1d4ed8" }),
            stroke: new Stroke({ color: "#fff", width: 1.5 }),
          }),
        }),
      });
      map.addLayer(layer);
      vectorSourceRef.current = source;
      vectorLayerRef.current = layer;
    })();

    return () => {
      cancelled = true;
      // Cleanup przy unmount: usuń warstwę, interakcję, overlays
      if (drawInteractionRef.current && map) {
        map.removeInteraction(drawInteractionRef.current);
      }
      if (vectorLayerRef.current && map) {
        map.removeLayer(vectorLayerRef.current);
      }
      if (cursorOverlayRef.current && map) {
        map.removeOverlay(cursorOverlayRef.current);
      }
      for (const ov of finalOverlaysRef.current) map.removeOverlay(ov);
      finalOverlaysRef.current = [];
      if (escHandlerRef.current) {
        window.removeEventListener("keydown", escHandlerRef.current);
        escHandlerRef.current = null;
      }
    };
  }, [map]);

  // ─────── Cleanup pomiarów ───────
  const clearAll = useCallback(() => {
    vectorSourceRef.current?.clear();
    for (const ov of finalOverlaysRef.current) map?.removeOverlay(ov);
    finalOverlaysRef.current = [];
    setHasMeasurements(false);
  }, [map]);

  // ─────── Tworzenie overlay etykiety (trwały po drawend) ───────
  const createLabelOverlay = useCallback((coord: number[], text: string) => {
    if (!olLibsRef.current || !map) return null;
    const el = document.createElement("div");
    el.className = "measure-label-final";
    el.textContent = text;
    el.style.cssText = `
      background: rgba(29, 78, 216, 0.92);
      color: #fff;
      padding: 3px 8px;
      border-radius: 4px;
      font-size: 12px;
      font-family: system-ui, -apple-system, sans-serif;
      font-weight: 600;
      white-space: nowrap;
      box-shadow: 0 1px 3px rgba(0,0,0,0.25);
      pointer-events: none;
      transform: translate(-50%, -120%);
    `;
    const overlay = new olLibsRef.current.Overlay({
      element: el,
      position: coord,
      positioning: "bottom-center",
      stopEvent: false,
    });
    map.addOverlay(overlay);
    return overlay;
  }, [map]);

  // ─────── Zmiana trybu: dodaj/usuń Draw interaction ───────
  useEffect(() => {
    if (!map || !olLibsRef.current || !vectorSourceRef.current) return;

    // Usuń poprzednią interakcję i cursor overlay
    if (drawInteractionRef.current) {
      map.removeInteraction(drawInteractionRef.current);
      drawInteractionRef.current = null;
    }
    if (cursorOverlayRef.current) {
      map.removeOverlay(cursorOverlayRef.current);
      cursorOverlayRef.current = null;
    }
    if (escHandlerRef.current) {
      window.removeEventListener("keydown", escHandlerRef.current);
      escHandlerRef.current = null;
    }

    if (mode === "off") return;

    const { Draw, Style, Fill, Stroke, CircleStyle, Overlay, getLength, getArea } =
      olLibsRef.current;

    // Styl rysowania (przerywana linia + półprzezroczyste wypełnienie)
    const drawStyle = new Style({
      fill: new Fill({ color: "rgba(37, 99, 235, 0.12)" }),
      stroke: new Stroke({ color: "#1d4ed8", width: 2, lineDash: [6, 4] }),
      image: new CircleStyle({
        radius: 5,
        fill: new Fill({ color: "#1d4ed8" }),
        stroke: new Stroke({ color: "#fff", width: 1.5 }),
      }),
    });

    const geomType = mode === "distance" ? "LineString" : "Polygon";
    const draw = new Draw({
      source: vectorSourceRef.current,
      type: geomType,
      style: drawStyle,
    });
    draw.set("isMeasureTool", true); // flag — żeby OlMap mógł zignorować singleclick

    // Cursor overlay — live tooltip z bieżącym wynikiem
    const cursorEl = document.createElement("div");
    cursorEl.style.cssText = `
      background: #1d4ed8;
      color: #fff;
      padding: 3px 8px;
      border-radius: 4px;
      font-size: 12px;
      font-family: system-ui, -apple-system, sans-serif;
      font-weight: 600;
      white-space: nowrap;
      box-shadow: 0 1px 3px rgba(0,0,0,0.25);
      pointer-events: none;
      opacity: 0.95;
    `;
    const cursorOverlay = new Overlay({
      element: cursorEl,
      offset: [12, 12],
      positioning: "top-left",
      stopEvent: false,
    });
    map.addOverlay(cursorOverlay);
    cursorOverlayRef.current = cursorOverlay;

    const projection = map.getView().getProjection();

    draw.on("drawstart", (evt: any) => {
      const geom = evt.feature.getGeometry();
      const updateTooltip = () => {
        const text = geomType === "LineString"
          ? formatDistance(getLength(geom, { projection }))
          : formatArea(getArea(geom, { projection }));
        cursorEl.textContent = text;
      };
      geom.on("change", updateTooltip);
      updateTooltip();
    });

    draw.on("drawend", (evt: any) => {
      const geom = evt.feature.getGeometry();
      let label = "";
      let anchor: number[];
      if (geomType === "LineString") {
        label = formatDistance(getLength(geom, { projection }));
        anchor = geom.getLastCoordinate();
      } else {
        label = formatArea(getArea(geom, { projection }));
        anchor = geom.getInteriorPoint().getCoordinates();
      }
      const ov = createLabelOverlay(anchor, label);
      if (ov) finalOverlaysRef.current.push(ov);
      setHasMeasurements(true);
      // Ukryj cursor tooltip natychmiast po zakończeniu
      cursorEl.textContent = "";
    });

    // Pointer move: pokaż tooltip nawet przed rozpoczęciem rysowania
    const pointerMoveKey = map.on("pointermove", (evt: any) => {
      if (evt.dragging) return;
      cursorOverlay.setPosition(evt.coordinate);
      if (!cursorEl.textContent) {
        cursorEl.textContent = geomType === "LineString"
          ? "Kliknij, aby dodać punkt (dbl-klik = koniec)"
          : "Kliknij, aby obrysować (dbl-klik = koniec)";
      }
    });

    // Escape → przerwij rysowanie
    const escHandler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (typeof draw.abortDrawing === "function") {
          draw.abortDrawing();
        }
      }
    };
    window.addEventListener("keydown", escHandler);
    escHandlerRef.current = escHandler;

    map.addInteraction(draw);
    drawInteractionRef.current = draw;

    return () => {
      // Rozłącz pointermove przy zmianie trybu
      if (pointerMoveKey) {
        const unByKey = (window as any).ol?.Observable?.unByKey;
        if (unByKey) unByKey(pointerMoveKey);
        else if (pointerMoveKey.target?.un) {
          pointerMoveKey.target.un(pointerMoveKey.type, pointerMoveKey.listener);
        }
      }
    };
  }, [mode, map, createLabelOverlay]);

  if (!map) return null;

  const containerStyle: React.CSSProperties = embedded
    ? {
        display: "flex",
        gap: 4,
        fontFamily: "system-ui, -apple-system, sans-serif",
        fontSize: 12,
        ...style,
      }
    : {
        position: "absolute",
        top: 12,
        right: 12,
        display: "flex",
        gap: 4,
        background: "#fff",
        padding: 4,
        borderRadius: 6,
        border: "1px solid #d1d5db",
        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
        fontFamily: "system-ui, -apple-system, sans-serif",
        fontSize: 12,
        zIndex: 1000,
        ...style,
      };

  return (
    <div style={containerStyle}>
      <button
        type="button"
        onClick={() => setMode((m) => (m === "distance" ? "off" : "distance"))}
        title="Zmierz odległość (klik = punkty, dbl-klik = koniec, Esc = anuluj)"
        style={btnStyle(mode === "distance")}>
        📏 Odległość
      </button>
      <button
        type="button"
        onClick={() => setMode((m) => (m === "area" ? "off" : "area"))}
        title="Zmierz powierzchnię (klik = punkty, dbl-klik = koniec, Esc = anuluj)"
        style={btnStyle(mode === "area")}>
        ⬟ Powierzchnia
      </button>
      <button
        type="button"
        onClick={clearAll}
        disabled={!hasMeasurements}
        title="Wyczyść wszystkie pomiary"
        style={{
          ...btnStyle(false),
          color: hasMeasurements ? "#dc2626" : "#9ca3af",
          cursor: hasMeasurements ? "pointer" : "not-allowed",
        }}>
        🗑 Wyczyść
      </button>
    </div>
  );
}

function btnStyle(active: boolean): React.CSSProperties {
  return {
    padding: "6px 10px",
    fontSize: 12,
    fontWeight: 600,
    background: active ? "#1d4ed8" : "#fff",
    color: active ? "#fff" : "#1f2937",
    border: `1px solid ${active ? "#1d4ed8" : "#d1d5db"}`,
    borderRadius: 4,
    cursor: "pointer",
    whiteSpace: "nowrap",
    fontFamily: "system-ui, -apple-system, sans-serif",
  };
}

function formatDistance(meters: number): string {
  if (!Number.isFinite(meters) || meters < 0) return "—";
  if (meters >= 1000) return `${(meters / 1000).toFixed(2)} km`;
  return `${meters.toFixed(1)} m`;
}

function formatArea(m2: number): string {
  if (!Number.isFinite(m2) || m2 < 0) return "—";
  if (m2 >= 1_000_000) return `${(m2 / 1_000_000).toFixed(3)} km²`;
  if (m2 >= 10_000) return `${(m2 / 10_000).toFixed(2)} ha`;
  return `${m2.toFixed(0)} m²`;
}

export default MeasureTools;
